import { useState, useRef, useCallback, useEffect } from "react";
import { X } from "lucide-react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { API } from "@/lib/api";

import { useAuth, useWebSocket } from "@/hooks/useAuth";
import { usePalletTypes } from "@/hooks/usePalletTypes";
import { useSavedList } from "@/hooks/useSavedList";
import { calculateMaterials } from "@/lib/calcEngine";
import "@/lib/syncQueue";
import { queueAndFlush } from "@/lib/syncQueue";
import { CutComponentsHistory } from "@/components/CutComponentsHistory";

import { AuthPage } from "@/components/AuthPage";
import { AppHeader } from "@/components/AppHeader";
import { PalletTypesSidebar } from "@/components/PalletTypesSidebar";
import { QuickCalculate } from "@/components/QuickCalculate";
import { CalculationResults } from "@/components/CalculationResults";
import { SavedList } from "@/components/SavedList";
import { LumberInventory } from "@/components/LumberInventory";
import { PalletFormDialog, DeleteConfirmDialog, ClearListDialog } from "@/components/PalletFormDialog";
import { AppTutorial, DEMO_PALLETS } from "@/components/AppTutorial";
import { PrivacyPolicy } from "@/components/PrivacyPolicy";
import { TermsOfService } from "@/components/TermsOfService";
import { TERMS_VERSION } from "@/lib/termsVersion";
import { DeleteAccountPage } from "@/components/DeleteAccountPage";
import { AddToHomeScreen } from "@/components/AddToHomeScreen";
import { Orders } from "@/components/Orders";
import { MonthlyCutHistory } from "@/components/MonthlyCutHistory";
import ResetPasswordPage from "@/components/ResetPasswordPage";
import SubscriptionPage from "@/components/SubscriptionPage";
import PaymentResult from "@/components/PaymentResult";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function App() {
  const tutorialModeRef = useRef(false);

  const { user, setUser, handleLogout, wsRef } = useAuth();
  const pt = usePalletTypes(user, tutorialModeRef);
  const { lastLocalUpdate } = useWebSocket(user, wsRef, pt.fetchPalletTypes, () => sl.fetchSavedList());
  const sl = useSavedList(user, lastLocalUpdate, tutorialModeRef);

  const [palletQuantity, setPalletQuantity] = useState("");
  const [calculationResult, setCalculationResult] = useState(null);

  // Clear calculator state when the signed-in account changes (or on sign out)
  const prevUserEmailRef = useRef(null);
  useEffect(() => {
    const email = user && user !== false ? user.email : null;
    if (prevUserEmailRef.current !== null && prevUserEmailRef.current !== email) {
      setPalletQuantity("");
      setCalculationResult(null);
      pt.setSelectedPalletType(null);
    }
    prevUserEmailRef.current = email;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  const [tutorialActive, setTutorialActive] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [authTutorial, setAuthTutorial] = useState(false);
  const [showMonthlyHistory, setShowMonthlyHistory] = useState(false);
  const [listHistView, setListHistView] = useState(null);
  const [showTermsNotice, setShowTermsNotice] = useState(() => localStorage.getItem('terms_notice_seen') !== TERMS_VERSION);
  const dismissTermsNotice = () => { localStorage.setItem('terms_notice_seen', TERMS_VERSION); setShowTermsNotice(false); };
  const [activePage, setActivePage] = useState(() => {
    const stored = localStorage.getItem('default_page');
    const migrate = { shop: 'calculator', cutting: 'cutlist', office: 'calculator' };
    return migrate[stored] || stored || 'calculator';
  });
  const [viewOnlyPages, setViewOnlyPages] = useState(() => {
    try { return JSON.parse(localStorage.getItem('viewonly_pages') || '[]'); } catch { return []; }
  });
  const [billing, setBilling] = useState(null);
  const [activeCutList, setActiveCutList] = useState(() => localStorage.getItem('active_cut_list') || 'all');
  const [addPick, setAddPick] = useState(null);

  useEffect(() => {
    const h = () => setActiveCutList(localStorage.getItem('active_cut_list') || 'all');
    window.addEventListener('cutlist-display-changed', h);
    return () => window.removeEventListener('cutlist-display-changed', h);
  }, []);

  // Global "Max Characters" alert: fires when typing/pasting past any input's maxLength
  useEffect(() => {
    let last = 0;
    const warn = () => { const n = Date.now(); if (n - last > 1200) { toast.error("Max Characters"); last = n; } };
    const isTarget = (el) => el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') && el.maxLength > 0;
    const onKey = (e) => {
      const el = e.target;
      if (!isTarget(el)) return;
      try {
        if (!(e.key && e.key.length === 1 && !e.ctrlKey && !e.metaKey) || el.value.length < el.maxLength) return;
        let selOk = true;
        try { selOk = el.selectionStart === el.selectionEnd; } catch {}
        if (selOk) warn();
      } catch {}
    };
    const onPaste = (e) => {
      const el = e.target;
      if (!isTarget(el)) return;
      try {
        const txt = e.clipboardData?.getData('text') || '';
        const sel = (el.selectionEnd || 0) - (el.selectionStart || 0);
        if (el.value.length - sel + txt.length > el.maxLength) warn();
      } catch {}
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('paste', onPaste, true);
    return () => { document.removeEventListener('keydown', onKey, true); document.removeEventListener('paste', onPaste, true); };
  }, []);

  // Anonymous visit ping (works signed out) — powers admin visit stats
  useEffect(() => {
    if (!navigator.onLine) return;
    let vid = localStorage.getItem('visitor_id');
    if (!vid) {
      vid = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem('visitor_id', vid);
    }
    axios.post(`${API}/track/visit`, { visitor_id: vid }).catch(() => {});
  }, []);

  const defaultListId = sl.cutLists[0]?.id;
  const itemsForList = (list) => sl.savedList.filter(i => !i.in_history && (i.list_id === list.id || (!i.list_id && list.id === defaultListId)));
  const selectedList = sl.cutLists.find(l => l.id === activeCutList);

  const handleAddToList = (compIdx = null) => {
    if (activeCutList === 'all' && sl.cutLists.length > 1) { setAddPick({ compIdx }); return; }
    const target = selectedList ? selectedList.id : defaultListId;
    if (compIdx === null) sl.addToList(calculationResult, target);
    else sl.addComponentToList(calculationResult, compIdx, target);
  };

  // Add from an order's pallet-details dialog (result computed there, not the calculator's)
  const handleCalcResultAddToCutList = (result, compIdx = null) => {
    if (activeCutList === 'all' && sl.cutLists.length > 1) { setAddPick({ compIdx, result }); return; }
    const target = selectedList ? selectedList.id : defaultListId;
    if (compIdx === null) sl.addToList(result, target);
    else sl.addComponentToList(result, compIdx, target);
  };

  const [orderAddPick, setOrderAddPick] = useState(null);
  const handleOrderAddToCutList = async (order) => {
    const items = (order.pallet_items && order.pallet_items.length > 0)
      ? order.pallet_items
      : (order.pallet_type_name ? [{ pallet_type_name: order.pallet_type_name, quantity: order.quantity || 0 }] : []);
    if (items.length === 0) { toast.error("This order has no pallets on it"); return; }
    const results = [];
    const skipped = [];
    let checkedSkipped = 0;
    for (const it of items) {
      if (it.checked || it.cut) { checkedSkipped++; continue; }
      const ptMatch = pt.palletTypes.find(p => p.name === it.pallet_type_name);
      const qty = parseInt(it.quantity) || 0;
      if (!ptMatch || qty <= 0) { skipped.push(it.pallet_type_name || 'Unknown'); continue; }
      // Shared local engine — identical math online and offline
      results.push(calculateMaterials(ptMatch, qty));
    }
    if (skipped.length > 0) toast.error(`Skipped (pallet type not found): ${skipped.join(', ')}`);
    if (results.length === 0) {
      if (checkedSkipped > 0) toast.info("All pallet types on this order are already checked off — nothing added");
      return;
    }
    if (sl.cutLists.length > 1) { setOrderAddPick({ results, customer: order.customer_name }); return; }
    // One batched add — every component appears instantly, even offline
    sl.addToList(results, defaultListId);
  };

  useEffect(() => {
    if (!user || user === false) { setBilling(null); return; }
    let cancelled = false;
    const fetchBilling = async () => {
      try {
        const { data } = await axios.get(`${API}/billing/status`);
        if (!cancelled) setBilling(data);
      } catch {
        if (!cancelled) setBilling({ access: "offline" });
      }
    };
    fetchBilling();
    const handler = () => fetchBilling();
    window.addEventListener('billing-changed', handler);
    return () => { cancelled = true; window.removeEventListener('billing-changed', handler); };
  }, [user]);

  useEffect(() => {
    const handler = () => {
      try { setViewOnlyPages(JSON.parse(localStorage.getItem('viewonly_pages') || '[]')); } catch {}
    };
    window.addEventListener('viewonly-changed', handler);
    return () => window.removeEventListener('viewonly-changed', handler);
  }, []);
  const isViewOnly = (page) => viewOnlyPages.includes(page);

  useEffect(() => {
    if (!user) return;
    let deviceId = localStorage.getItem('device_id');
    if (!deviceId) {
      deviceId = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem('device_id', deviceId);
    }
    const beat = async () => {
      if (!navigator.onLine) return;
      let model = "", platformVersion = "";
      try {
        if (navigator.userAgentData?.getHighEntropyValues) {
          const h = await navigator.userAgentData.getHighEntropyValues(["model", "platformVersion"]);
          model = h.model || "";
          platformVersion = h.platformVersion || "";
        }
      } catch {}
      axios.post(`${API}/devices/heartbeat`, {
        device_id: deviceId, user_agent: navigator.userAgent, platform: navigator.platform || "",
        model, platform_version: platformVersion, screen: `${window.screen.width}x${window.screen.height}`,
      }).then(({ data }) => {
        try {
          localStorage.setItem('is_owner_device', data.is_owner ? '1' : '0');
          localStorage.setItem('owner_device_ua', data.owner_user_agent || '');
          localStorage.setItem('owner_device_model', data.owner_model || '');
          const pages = JSON.stringify(data.viewonly_pages || []);
          if (localStorage.getItem('viewonly_pages') !== pages) {
            localStorage.setItem('viewonly_pages', pages);
            window.dispatchEvent(new Event('viewonly-changed'));
          }
          window.dispatchEvent(new Event('owner-changed'));
        } catch {}
      }).catch(() => {});
    };
    beat();
    const iv = setInterval(beat, 20000);
    return () => clearInterval(iv);
  }, [user]);

  // Pre-warm offline caches so Orders/Lumber tabs work offline even if never opened online
  useEffect(() => {
    if (!user || user === false || !navigator.onLine) return;
    axios.get(`${API}/orders`).then(({ data }) => { try { localStorage.setItem('orders_cache', JSON.stringify(data)); } catch {} }).catch(() => {});
    axios.get(`${API}/lumber-inventory`).then(({ data }) => { try { localStorage.setItem('lumber_cache', JSON.stringify(data)); } catch {} }).catch(() => {});
    axios.get(`${API}/lumber-inventory/history`).then(({ data }) => { try { localStorage.setItem('lumber_history_cache', JSON.stringify(data)); } catch {} }).catch(() => {});
  }, [user]);

  // Tutorial: guided page-by-page tour, no data changes
  const startTutorial = useCallback(() => {
    setTutorialActive(true);
  }, []);

  const endTutorial = useCallback(() => {
    setTutorialActive(false);
  }, []);

  // Calculator — one shared local engine for online, offline and tutorial.
  // No network request is ever needed to calculate.
  const handleCalculate = async () => {
    if (!pt.selectedPalletType) { toast.error("Please select a pallet type"); return; }
    const qty = parseInt(palletQuantity);
    if (!qty || qty < 1) { toast.error("Please enter a valid quantity"); return; }
    setCalculationResult(calculateMaterials(pt.selectedPalletType, qty));
    toast.success("Calculation complete!");
  };

  // Standalone privacy policy URL for Google Play
  if (window.location.pathname === '/privacy') {
    return <PrivacyPolicy onBack={() => { window.location.pathname = '/'; }} />;
  }

  // Standalone terms of service URL
  if (window.location.pathname === '/terms') {
    return <TermsOfService onBack={() => { window.location.pathname = '/'; }} />;
  }

  // Stripe checkout return pages
  if (window.location.pathname === '/payment/success') {
    return <PaymentResult mode="success" />;
  }
  if (window.location.pathname === '/payment/cancel') {
    return <PaymentResult mode="cancel" />;
  }

  // Password reset landing page (from email link)
  if (window.location.pathname === '/reset-password') {
    return <ResetPasswordPage />;
  }

  // Standalone delete account URL for Google Play
  if (window.location.pathname === '/delete-account') {
    return <DeleteAccountPage onBack={() => { window.location.pathname = '/'; }} onDeleted={() => setUser(false)} />;
  }

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center">
        <div className="text-zinc-500 text-lg">Loading...</div>
      </div>
    );
  }
  if (user === false) {
    // Terms/Privacy render as overlays so the sign-in/sign-up form keeps its typed values
    return (
      <>
        <AuthPage onLogin={setUser} onShowPrivacy={() => setShowPrivacy(true)} onShowTerms={() => setShowTerms(true)} onStartTutorial={() => setAuthTutorial(true)} />
        {showPrivacy && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-[#F4F4F5]" data-testid="auth-privacy-overlay">
            <PrivacyPolicy onBack={() => setShowPrivacy(false)} />
          </div>
        )}
        {showTerms && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-[#F4F4F5]" data-testid="auth-terms-overlay">
            <TermsOfService onBack={() => setShowTerms(false)} />
          </div>
        )}
        <AppTutorial active={authTutorial} onFinish={() => setAuthTutorial(false)} preLogin />
        <AddToHomeScreen />
      </>
    );
  }

  // Render guards (logged-in)
  if (showPrivacy) {
    return <PrivacyPolicy onBack={() => setShowPrivacy(false)} />;
  }
  if (showTerms) {
    return <TermsOfService onBack={() => setShowTerms(false)} />;
  }

  // Subscription paywall (locked accounts only; offline users are never locked out)
  if (billing && billing.access === 'locked') {
    return (
      <>
        <Toaster position="top-right" richColors />
        <SubscriptionPage user={user} billing={billing} onLogout={handleLogout} />
      </>
    );
  }

  // Duplicate a component from current pallet form to another pallet type
  const duplicateComponentToPallet = async (component, targetPallet) => {
    const newComponent = {
      name: component.name,
      quantity: parseFloat(component.quantity) || 0,
      length: component.length ? parseFloat(component.length) : null,
      height: component.height ? parseFloat(component.height) : null,
      thickness: component.thickness ? parseFloat(component.thickness) : null,
      notched: component.notched,
      notch_length: component.notch_length ? parseFloat(component.notch_length) : null,
      pcs_per_min: component.pcs_per_min ? parseFloat(component.pcs_per_min) : null,
      board_footage: component.board_footage ? parseFloat(component.board_footage) : null,
      cost: component.cost ? parseFloat(component.cost) : null,
      lumber: component.lumber?.filter(l => l.length || l.quantity || l.description).map(l => ({
        length: l.length ? parseFloat(l.length) : null,
        quantity: l.quantity ? parseFloat(l.quantity) : null,
        description: l.description || null,
        thickness: l.thickness ? parseFloat(l.thickness) : null,
      })) || null,
      skids: component.skids ? parseFloat(component.skids) : null,
      extras: component.extras || null,
    };
    const updatedComponents = [...(targetPallet.components || []), newComponent];
    try {
      await axios.put(`${API}/pallet-types/${targetPallet.id}`, { ...targetPallet, components: updatedComponents });
      await pt.fetchPalletTypes();
      toast.success(`"${component.name}" duplicated to ${targetPallet.name}`);
      // Open the target pallet's edit form with the new component
      setTimeout(() => pt.openEditForm({ ...targetPallet, components: updatedComponents }), 300);
    } catch {
      toast.error("Failed to duplicate component");
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5]" data-testid="app-container">
      <Toaster position="top-right" richColors />

      <AppHeader
        user={user} isOnline={pt.isOnline} pendingSync={pt.pendingSync}
        syncWithServer={pt.syncWithServer} handleLogout={handleLogout} openCreateForm={pt.openCreateForm}
        onManualSync={() => pt.handleManualSync(sl.fetchSavedList)} onUserUpdate={setUser}
        onStartTutorial={startTutorial} onShowPrivacy={() => setShowPrivacy(true)} onShowTerms={() => setShowTerms(true)}
        onShowMonthlyHistory={() => setShowMonthlyHistory(true)} slPending={sl.pendingSyncCount}
        activePage={activePage} setActivePage={setActivePage}
      />

      {showTermsNotice && (
        <div className="bg-amber-50 border-b border-amber-200" data-testid="terms-notice-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-3">
            <p className="text-xs text-amber-900">
              We've updated our <b>Terms of Service</b>.
              <button onClick={() => { dismissTermsNotice(); setShowTerms(true); }}
                className="ml-2 font-bold underline text-amber-900 hover:text-amber-700" data-testid="terms-notice-review-btn">
                Review the changes
              </button>
            </p>
            <button onClick={dismissTermsNotice} className="p-1 text-amber-500 hover:text-amber-800 flex-shrink-0" title="Dismiss" data-testid="terms-notice-dismiss-btn">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activePage === 'calculator' && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <PalletTypesSidebar
              palletTypes={pt.palletTypes} selectedPalletType={pt.selectedPalletType}
              setSelectedPalletType={pt.setSelectedPalletType} searchTerm={pt.searchTerm}
              setSearchTerm={pt.setSearchTerm} openEditForm={pt.openEditForm} setDeleteConfirmId={pt.setDeleteConfirmId}
              openCreateForm={pt.openCreateForm}
              officeMode={isViewOnly('calculator')}
            />

            <QuickCalculate
              palletQuantity={palletQuantity} setPalletQuantity={setPalletQuantity}
              selectedPalletType={pt.selectedPalletType} loading={pt.loading} handleCalculate={handleCalculate}
            />

            <CalculationResults
              calculationResult={calculationResult}
              addToList={() => handleAddToList(null)}
              addComponentToList={(idx) => handleAddToList(idx)}
              hideAddToList={isViewOnly('calculator')}
            />
          </div>
        )}

        {activePage === 'cutlist' && (
          activeCutList !== 'all' && selectedList ? (
            <SavedList
              key={selectedList.id}
              savedList={itemsForList(selectedList)} listTitle={selectedList.name} listId={selectedList.id}
              dragIdx={sl.dragIdx} toggleCheck={sl.toggleCheck}
              removeFromList={sl.removeFromList} moveItem={sl.moveItem}
              sortByThickness={sl.sortByThickness} setClearListConfirm={sl.setClearListConfirm}
              updateItemCustomQty={sl.updateItemCustomQty} updateItemStopwatch={sl.updateItemStopwatch}
              toggleSavedForLater={sl.toggleSavedForLater} moveToHistory={sl.moveToHistory}
              onShowHistory={(type) => setListHistView({ type, listId: selectedList.id, listName: selectedList.name })}
              readOnly={isViewOnly('cutlist')}
            />
          ) : (
            <div className="space-y-4">
              {sl.cutLists.map(list => (
                <SavedList
                  key={list.id}
                  savedList={itemsForList(list)} listTitle={list.name} listId={list.id}
                  collapsible={sl.cutLists.length > 1}
                  dragIdx={sl.dragIdx} toggleCheck={sl.toggleCheck}
                  removeFromList={sl.removeFromList} moveItem={sl.moveItem}
                  sortByThickness={sl.sortByThickness} setClearListConfirm={sl.setClearListConfirm}
                  updateItemCustomQty={sl.updateItemCustomQty} updateItemStopwatch={sl.updateItemStopwatch}
                  toggleSavedForLater={sl.toggleSavedForLater} moveToHistory={sl.moveToHistory}
                  onShowHistory={(type) => setListHistView({ type, listId: list.id, listName: list.name })}
                  readOnly={isViewOnly('cutlist')}
                />
              ))}
              {sl.savedList.filter(i => !i.in_history).length === 0 && (
                <div className="px-4 py-2" data-testid="cut-list-empty-message">
                  <p className="text-sm font-bold text-zinc-600 mb-1">NO ITEMS IN CUT LIST</p>
                  <p className="text-sm text-zinc-500 leading-relaxed">
                    Go to Calculator page and make a calculation, then Add To Cut List.
                    <br />
                    Or go to Orders page, click on an order&apos;s options menu, and Add To Cut List.
                  </p>
                </div>
              )}
            </div>
          )
        )}

        {activePage === 'lumber' && (
          <LumberInventory readOnly={isViewOnly('lumber')} />
        )}

        {activePage === 'orders' && (
          <Orders palletTypes={pt.palletTypes} readOnly={isViewOnly('orders')} onAddToCutList={handleOrderAddToCutList} onAddCalcToCutList={handleCalcResultAddToCutList} />
        )}
      </main>

      <PalletFormDialog
        isFormOpen={pt.isFormOpen} setIsFormOpen={pt.setIsFormOpen} editingPallet={pt.editingPallet}
        formData={pt.formData} setFormData={pt.setFormData} addComponent={pt.addComponent}
        removeComponent={pt.removeComponent} updateComponent={pt.updateComponent}
        updateLumber={pt.updateLumber} handleCreateOrUpdate={pt.handleCreateOrUpdate} loading={pt.loading}
        onImageUpload={pt.handleImageUpload} onImageRemove={pt.handleImageRemove}
        tutorialMode={tutorialActive}
        palletTypes={pt.palletTypes} onDuplicateComponentToPallet={duplicateComponentToPallet}
      />

      <DeleteConfirmDialog deleteConfirmId={pt.deleteConfirmId} setDeleteConfirmId={pt.setDeleteConfirmId} handleDelete={pt.handleDelete} />
      <ClearListDialog clearListConfirm={sl.clearListConfirm} setClearListConfirm={sl.setClearListConfirm} clearList={sl.clearList}
        savedListCount={(() => {
          const target = typeof sl.clearListConfirm === 'string' ? sl.clearListConfirm : null;
          const def = sl.cutLists[0]?.id;
          return sl.savedList.filter(i => !i.saved_for_later && !i.in_history && (!target || i.list_id === target || (!i.list_id && target === def))).length;
        })()} />

      {/* Pick which cut list an order's components go into */}
      <AlertDialog open={!!orderAddPick} onOpenChange={(o) => { if (!o) setOrderAddPick(null); }}>
        <AlertDialogContent className="bg-white border border-zinc-200 rounded-none max-w-sm" data-testid="order-cutlist-picker-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>Add to which cut list?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-zinc-500">
              Pick the cut list the components from {orderAddPick?.customer ? `"${orderAddPick.customer}"` : 'this order'} should go into.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            {sl.cutLists.map(list => (
              <button key={list.id}
                onClick={async () => {
                  const results = orderAddPick.results;
                  setOrderAddPick(null);
                  sl.addToList(results, list.id);
                }}
                className="w-full text-left p-3 border border-zinc-300 hover:border-blue-500 hover:bg-blue-50 transition-colors text-sm font-semibold text-zinc-800"
                data-testid={`order-pick-list-${list.id}`}>
                {list.name}
              </button>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-zinc-300">Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Pick which cut list to add to (when showing all lists) */}
      <AlertDialog open={!!addPick} onOpenChange={(o) => { if (!o) setAddPick(null); }}>
        <AlertDialogContent className="bg-white border border-zinc-200 rounded-none max-w-sm" data-testid="cutlist-picker-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>Add to which cut list?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-zinc-500">
              Pick the cut list {addPick?.compIdx === null ? 'these components' : 'this component'} should go into.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            {sl.cutLists.map(list => (
              <button key={list.id}
                onClick={() => {
                  const res = addPick.result || calculationResult;
                  if (addPick.compIdx === null) sl.addToList(res, list.id);
                  else sl.addComponentToList(res, addPick.compIdx, list.id);
                  setAddPick(null);
                }}
                className="w-full text-left p-3 border border-zinc-300 hover:border-blue-500 hover:bg-blue-50 transition-colors text-sm font-semibold text-zinc-800"
                data-testid={`pick-list-${list.id}`}>
                {list.name}
              </button>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-zinc-300">Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AppTutorial active={tutorialActive} onFinish={endTutorial} setActivePage={setActivePage} />
      <MonthlyCutHistory open={showMonthlyHistory} onOpenChange={setShowMonthlyHistory} savedList={sl.savedList}
        defaultListId={defaultListId} onRemoveItem={(id) => { sl.updateItemStopwatch(id, { hidden_from_monthly: true }); toast.success("Removed from monthly history"); }} />

      {/* Per-cut-list histories (from the History button on each list) */}
      {listHistView?.type === 'monthly' && (
        <MonthlyCutHistory open onOpenChange={(o) => { if (!o) setListHistView(null); }} savedList={sl.savedList}
          listId={listHistView.listId} listName={listHistView.listName} defaultListId={defaultListId}
          onRemoveItem={(id) => { sl.updateItemStopwatch(id, { hidden_from_monthly: true }); toast.success("Removed from monthly history"); }} />
      )}
      {listHistView?.type === 'components' && (
        <CutComponentsHistory open onOpenChange={(o) => { if (!o) setListHistView(null); }}
          items={sl.savedList.filter(i => i.in_history && ((i.list_id || defaultListId) === listHistView.listId))}
          listName={listHistView.listName}
          onRestore={(id) => { sl.updateItemStopwatch(id, { in_history: false, history_moved_at: null, hidden_from_monthly: false }); toast.success("Added back to cut list"); }}
          onDelete={(id) => {
            sl.setSavedList(prev => prev.filter(i => i.id !== id));
            queueAndFlush({ method: "delete", path: `/saved-list/${id}?restore=false`, entity: "saved_list", entityId: id });
            toast.success("Deleted");
          }} />
      )}
      <AddToHomeScreen />
    </div>
  );
}

export default App;
